import { useState } from "react";
import { toast } from "sonner";
import { AlertOctagon, AlertCircle, FileText, Filter } from "lucide-react";
import { qmsr } from "@/data/fixtures";
import SourceChip from "@/components/SourceChip";
import ApprovalModal from "@/components/ApprovalModal";

const severityMap = {
  critical: {
    label: "Critical",
    icon: AlertOctagon,
    color: "var(--mos-red)",
    bg: "var(--mos-red-bg)",
    border: "var(--mos-red-border)",
  },
  high: {
    label: "High",
    icon: AlertCircle,
    color: "var(--mos-amber)",
    bg: "var(--mos-amber-bg)",
    border: "var(--mos-amber-border)",
  },
  medium: {
    label: "Medium",
    icon: FileText,
    color: "#B45309",
    bg: "#FEF3C7",
    border: "#FDE68A",
  },
};

const driftLabel = {
  "evidence-gap": "Evidence gap",
  terminology: "Terminology drift",
  revision: "Revision drift",
};

export default function QMSRCrosswalk() {
  const [sortBy, setSortBy] = useState("severity");
  const [remediationTarget, setRemediationTarget] = useState(null);

  const rows = [...qmsr.findings].sort((a, b) => {
    if (sortBy === "severity") {
      const rank = { critical: 0, high: 1, medium: 2 };
      return rank[a.severity] - rank[b.severity];
    }
    if (sortBy === "age") return b.ageDays - a.ageDays;
    return 0;
  });

  const onExport = () => toast.success("QMSR readiness register exported · CSV", { duration: 4000 });
  const onDraftRemediation = (item) => setRemediationTarget(item);
  const onApproveRemediation = () => {
    toast.success(
      `Remediation task drafted · ${remediationTarget.clause} · pending assignment`,
      { duration: 4200 }
    );
    setRemediationTarget(null);
  };

  return (
    <div data-testid="page-qmsr-crosswalk" className="space-y-6">
      {/* Persistent banner · SP-05 */}
      <div
        className="rounded-md border px-4 py-2.5 text-[12.5px]"
        style={{
          background: "#F1F5F9",
          borderColor: "var(--mos-border)",
          color: "var(--mos-text-muted)",
        }}
        data-testid="qmsr-banner"
      >
        <span className="font-medium text-slate-700">Note · </span>
        {qmsr.banner}
      </div>

      {/* Header + summary */}
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-2">
            Inspection Readiness Register
          </div>
          <h2 className="text-[26px] leading-tight font-semibold text-slate-900 tracking-tight">
            ISO 13485:2016 as incorporated by QMSR
          </h2>
          <div className="mt-2 text-[13px] text-slate-600 flex items-center gap-2 flex-wrap">
            Content pack{" "}
            <SourceChip system="MasterControl" record={qmsr.clauseMapVersion} />
            {" "}· {qmsr.findings.length} findings · {qmsr.summary.netChange}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <SummaryPill label="Critical" count={qmsr.summary.critical} tone="critical" />
          <SummaryPill label="High" count={qmsr.summary.high} tone="high" />
          <SummaryPill label="Medium" count={qmsr.summary.medium} tone="medium" />
          <button
            type="button"
            onClick={onExport}
            className="mos-btn mos-btn-secondary"
            data-testid="btn-export-qmsr"
          >
            Export
          </button>
        </div>
      </div>

      {/* Sort control */}
      <div className="flex items-center gap-3 text-[12.5px] text-slate-500">
        <Filter size={13} />
        <span>Sort</span>
        <button
          type="button"
          onClick={() => setSortBy("severity")}
          data-testid="qmsr-sort-severity"
          className={
            "px-2 py-1 rounded " +
            (sortBy === "severity" ? "bg-slate-100 text-slate-900 font-medium" : "hover:text-slate-800")
          }
        >
          Severity
        </button>
        <button
          type="button"
          onClick={() => setSortBy("age")}
          data-testid="qmsr-sort-age"
          className={
            "px-2 py-1 rounded " +
            (sortBy === "age" ? "bg-slate-100 text-slate-900 font-medium" : "hover:text-slate-800")
          }
        >
          Age
        </button>
      </div>

      {/* Register table */}
      <div className="mos-surface rounded-md overflow-hidden" data-testid="qmsr-register">
        <table className="w-full text-left">
          <thead>
            <tr
              className="text-[11px] uppercase tracking-wider text-slate-500"
              style={{ background: "#FAFBFC" }}
            >
              <th className="py-3 pl-5 pr-3 font-semibold w-[150px]">Clause</th>
              <th className="py-3 px-3 font-semibold w-[120px]">Severity</th>
              <th className="py-3 px-3 font-semibold w-[140px]">Drift class</th>
              <th className="py-3 px-3 font-semibold">Finding</th>
              <th className="py-3 px-3 font-semibold w-[190px]">Document · revision</th>
              <th className="py-3 px-3 font-semibold w-[80px]">Age</th>
              <th className="py-3 px-5 font-semibold w-[130px]">Disposition</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f, i) => {
              const s = severityMap[f.severity];
              const Icon = s.icon;
              return (
                <tr
                  key={f.id}
                  className="border-t align-top"
                  style={{
                    borderColor: "var(--mos-border)",
                    background: i % 2 === 1 ? "#FBFBFC" : "transparent",
                  }}
                  data-testid={`qmsr-finding-${f.id}`}
                >
                  <td className="py-4 pl-5 pr-3 mos-mono text-[13px] text-slate-900 whitespace-nowrap">
                    {f.clause}
                    <div className="text-[11.5px] text-slate-500 font-sans mt-1">
                      {f.clauseTitle}
                    </div>
                  </td>
                  <td className="py-4 px-3">
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase px-1.5 py-0.5 rounded border"
                      style={{ color: s.color, background: s.bg, borderColor: s.border }}
                    >
                      <Icon size={11} />
                      {s.label}
                    </span>
                  </td>
                  <td className="py-4 px-3 text-[12.5px] text-slate-700 whitespace-nowrap">
                    {driftLabel[f.driftClass]}
                  </td>
                  <td className="py-4 px-3 text-[13px] text-slate-700 leading-snug">
                    {f.finding}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      <SourceChip system={f.source.system} record={f.source.record} />
                      <span className="text-[11px] text-slate-400">
                        confidence <span className="mos-mono">{Math.round(f.confidence * 100)}%</span>
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-3 text-[12.5px] text-slate-800">
                    {f.documentRef ? (
                      <span className="mos-mono">{f.documentRef}</span>
                    ) : (
                      <span className="text-slate-400 italic">no current document</span>
                    )}
                  </td>
                  <td className="py-4 px-3 mos-mono text-[13px] text-slate-800 whitespace-nowrap">
                    {f.ageDays}d
                  </td>
                  <td className="py-4 px-5">
                    <button
                      type="button"
                      onClick={() => onDraftRemediation(f)}
                      className="text-[12.5px] text-[color:var(--mos-accent)] hover:underline font-medium"
                      data-testid={`btn-draft-remediation-${f.id}`}
                    >
                      Draft task
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[11.5px] text-slate-500">
        Findings below the {"75%"} confidence threshold are queued for human review and never
        auto-published to the register (SP-02, F1-AI-01). Every disposition writes to the audit log.
      </p>

      <ApprovalModal
        open={!!remediationTarget}
        onCancel={() => setRemediationTarget(null)}
        onApprove={onApproveRemediation}
        title="Draft remediation task"
        rationale="Draft is generated from findings only and is held for human assignment. No source-system writes occur until a named owner accepts the task."
        approver="R. Diaz"
        action={
          remediationTarget
            ? `Draft remediation for ${remediationTarget.clause}`
            : ""
        }
        systemsAffected={["MasterControl · task queue"]}
        auditNote="Draft creation and its assignment will each write to the audit log."
        confirmLabel="Draft and queue"
      />
    </div>
  );
}

function SummaryPill({ label, count, tone }) {
  const s = severityMap[tone] || severityMap.medium;
  return (
    <div
      className="flex items-center gap-2 rounded-md border px-3 py-1.5"
      style={{ borderColor: s.border, background: s.bg }}
      data-testid={`qmsr-summary-${tone}`}
    >
      <span className="mos-mono text-[16px] font-semibold" style={{ color: s.color }}>
        {count}
      </span>
      <span className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: s.color }}>
        {label}
      </span>
    </div>
  );
}
